// src/pages/Gamification.tsx
import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
    Container, Typography, Box, Card, CardContent,
    Button, Collapse, useTheme, useMediaQuery
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Leaderboard';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import ToggleCard from '../components/ToggleCard';

type Challenge = {
    title: string;
    points: number;
    completed: boolean;
};

type Badge = {
    title: string;
    subtitle: string;
    level: number;
    progress: number;
};

const Gamification: React.FC = () => {
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
    const [redeemed, setRedeemed] = useState(false);
    const [tab, setTab] = useState<'current' | 'badges'>('current');
    const confirmRef = useRef<HTMLDivElement>(null);

    // Full data object
    const data = {
        // Left panel
        levelName: 'Forest Guardian',
        levelNumber: 7,
        xpProgress: 8540,
        xpMax: 10000,
        todayChallenges: [
            { title: 'Enable Paperless Billing', points: 30, completed: false },
            { title: 'Round Up a Purchase for Impact', points: 50, completed: true },
            { title: 'Track 10,000 Steps', points: 10, completed: false },
            { title: 'Support a sustainable project', points: 100, completed: true },
        ] as Challenge[],
        stats: { dailyStreak: 12, totalPoints: 2540, badgesEarned: 3 },

        // Right panel
        availablePoints: 2540,
        redeemOptions: [
            { id: 'trees', title: 'Restore Mangroves', cost: '500 pts = 1 mangrove', button: 'Restore 5 Mangroves' },
            { id: 'school', title: 'Fund Education', cost: '1000 pts = 1 day of school', button: 'Fund 2 Days of School' },
            { id: 'ocean', title: 'Ocean Cleanup', cost: '300 pts = 1kg plastic', button: 'Remove 8kg Plastic' },
        ],
        badges: [
            { title: 'Mangrove Champion', subtitle: 'Restore 10 mangroves in the real world', progress: 75, level: 2 },
            { title: 'Climate Hero', subtitle: 'Complete 5 eco-challenges', progress: 40, level: 1 },
            { title: 'Ocean Guardian', subtitle: 'Cleanup 10kg of plastic', progress: 100, level: 3 },
        ] as Badge[],
        totalImpact: { mangrovesRestored: 12, plasticRemoved: 23, daysOfSchool: 2 }
    };

    const handleRedeem = () => setRedeemed(true);

    const renderIcon = (id: string) => {
        if (id === 'trees') return <Typography sx={{ fontSize: 20, mr: 1 }}>🌱</Typography>;
        if (id === 'ocean') return <Typography sx={{ fontSize: 20, mr: 1 }}>📚</Typography>;
        if (id === 'school') return <Typography sx={{ fontSize: 20, mr: 1 }}>🌊</Typography>;
        return null;
    };

    return (
        <Container maxWidth="lg">
            <Typography variant="h3" sx={{ color: 'rgb(79,111,82)', mb: 1 }}>
                Gamified Sustainability
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 4 }}>
                Complete eco-challenges and earn rewards that fund real-world regeneration with our API
            </Typography>

            <Box display="flex" flexDirection={isDesktop ? 'row' : 'column'} gap={4}>
                {/* Left: Handprint Challenges */}
                <Box flex={1}>
                    <Card>
                        <CardContent>
                            {/* Header + JSON toggle */}
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 700, display: 'flex', alignItems: 'center' }}
                                >
                                    <FingerprintIcon sx={{ mr: 1, color: 'rgb(101,183,65)' }} />
                                    Regenerative Challenges
                                </Typography>
                            </Box>
                            <ToggleCard
                                title="&nbsp;"
                                code={{
                                    levelName: data.levelName,
                                    levelNumber: data.levelNumber,
                                    xpProgress: data.xpProgress,
                                    xpMax: data.xpMax,
                                    todayChallenges: data.todayChallenges,
                                    stats: data.stats
                                }}
                            >

                                {/* Level & XP */}
                                <Card sx={{ backgroundColor: 'rgb(239,253,219)', p: 2, mb: 3 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography fontWeight={700}>
                                            Your Level: {data.levelName}
                                        </Typography>
                                        <Box
                                            component="span"
                                            sx={{
                                                backgroundColor: 'rgb(101,183,65)',
                                                color: 'white',
                                                borderRadius: 2,
                                                px: 1.5,
                                                py: 0.5,
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            Level {data.levelNumber}
                                        </Box>
                                    </Box>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography variant="body2">XP Progress</Typography>
                                        <Typography variant="body2">
                                            {data.xpProgress.toLocaleString()} / {data.xpMax.toLocaleString()}
                                        </Typography>
                                    </Box>
                                    <Box display="flex" height={8} borderRadius={4} overflow="hidden">
                                        <Box
                                            flex={data.xpProgress}
                                            bgcolor="rgb(127,191,77)"
                                        />
                                        <Box
                                            flex={data.xpMax - data.xpProgress}
                                            bgcolor="rgb(68,83,62)"
                                        />
                                    </Box>
                                </Card>

                                {/* Today's Challenges */}
                                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
                                    Today's Eco-Challenges
                                </Typography>
                                <Box display="flex" flexDirection="column" gap={2} mb={3}>
                                    {data.todayChallenges.map((c, i) => (
                                        <Card
                                            key={i}
                                            variant="outlined"
                                            sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                                        >
                                            <Box>
                                                <Typography fontWeight={700}>{c.title}</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    +{c.points} EarthPoints
                                                </Typography>
                                            </Box>
                                            {c.completed
                                                ? <Button variant="contained" size="small" disabled>Completed</Button>
                                                : <Button variant="outlined" size="small">Complete</Button>
                                            }
                                        </Card>
                                    ))}
                                </Box>

                                {/* Stats */}
                                <Box display="flex" gap={2} mb={3}>
                                    {[
                                        { label: 'Daily Streak', value: data.stats.dailyStreak, unit: 'Daily streaks' },
                                        { label: 'Total Points', value: data.stats.totalPoints.toLocaleString(), unit: 'EarthPoints' },
                                        { label: 'Badges', value: data.stats.badgesEarned, unit: 'Badges earned' },
                                    ].map((s, i) => (
                                        <Box
                                            key={i}
                                            flex={1}
                                            bgcolor="rgb(239,253,219)"
                                            borderRadius={1}
                                            p={2}
                                            textAlign="center"
                                        >
                                            <Typography fontWeight={700}>{s.value}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {s.unit}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>

                                <Button
                                    variant="contained"
                                    fullWidth
                                    sx={{ py: 1.5, fontWeight: 700 }}
                                    disabled={redeemed}
                                    onClick={handleRedeem}
                                >
                                    Redeem Points for Impact
                                </Button>
                            </ToggleCard>
                        </CardContent>
                    </Card>
                </Box>

                {/* Right: Real-World Impact */}
                <Box flex={1} ref={confirmRef} sx={{ scrollMarginTop: '80px' }}>
                    <Collapse
                        in={redeemed}
                        timeout={600}
                        unmountOnExit
                        onEntered={() => confirmRef.current?.scrollIntoView({ behavior: 'smooth' })}
                    >
                        <Card>
                            <CardContent>
                                {/* Header + JSON toggle */}
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                    <Typography
                                        variant="h5"
                                        sx={{ fontWeight: 700, display: 'flex', alignItems: 'center' }}
                                    >
                                        <EmojiEventsIcon sx={{ mr: 1, color: 'rgb(101,183,65)' }} />
                                        Your Real-World Impact
                                    </Typography>
                                </Box>
                                <Typography variant="subtitle1" color="text.secondary" mb={3}>
                                    See how your actions create a regenerative change
                                </Typography>
                                <ToggleCard
                                    title="&nbsp;"
                                    code={{
                                        availablePoints: data.availablePoints,
                                        redeemOptions: data.redeemOptions,
                                        badges: data.badges,
                                        totalImpact: data.totalImpact,
                                    }}
                                >

                                    {/* Tabs */}
                                    <Box display="flex" borderRadius={1} overflow="hidden" mb={3}>
                                        {(['current', 'badges'] as const).map(key => (
                                            <Box
                                                key={key}
                                                flex={1}
                                                textAlign="center"
                                                py={1}
                                                onClick={() => setTab(key)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    backgroundColor: tab === key ? 'white' : 'rgb(239,253,219)',
                                                    color: tab === key ? 'text.primary' : 'text.secondary',
                                                }}
                                            >
                                                {key === 'current' ? 'Current Impact' : 'Your Badges'}
                                            </Box>
                                        ))}
                                    </Box>

                                    {/* Current Impact Panel */}
                                    {tab === 'current' && (
                                        <>
                                            <Card sx={{ backgroundColor: 'rgb(239,253,219)', p: 2, mb: 3, textAlign: 'center' }}>
                                                <Typography fontWeight={700}>Available EarthPoints</Typography>
                                                <Typography variant="h3" sx={{ color: 'rgb(79,111,82)', fontWeight: 700, my: 1 }}>
                                                    {data.availablePoints.toLocaleString()} pts
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Choose how to use your points for regenerative impact
                                                </Typography>
                                            </Card>

                                            <Typography variant="subtitle1" fontWeight={700} mb={2}>
                                                Redeem For Real Impact
                                            </Typography>
                                            <Box display="flex" flexDirection={isDesktop ? 'row' : 'column'} gap={2} mb={3}>
                                                {data.redeemOptions.map(opt => (
                                                    <Card key={opt.id} variant="outlined" sx={{ flex: 1 }}>
                                                        <CardContent sx={{ textAlign: 'center' }}>
                                                            {renderIcon(opt.id)}
                                                            <Typography fontWeight={700} mt={1}>{opt.title}</Typography>
                                                            <Typography variant="body2" color="text.secondary" mb={2}>
                                                                {opt.cost}
                                                            </Typography>
                                                            <Button variant="contained" fullWidth>
                                                                {opt.button}
                                                            </Button>
                                                        </CardContent>
                                                    </Card>
                                                ))}
                                            </Box>
                                        </>
                                    )}

                                    {/* Badges Panel */}
                                    {tab === 'badges' && (
                                        <>
                                            {data.badges.map((b, i) => (
                                                <Box key={i} mb={2}>
                                                    <Card variant="outlined">
                                                        <CardContent>
                                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                                <Box>
                                                                    <Typography fontWeight={700}>{b.title}</Typography>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        {b.subtitle}
                                                                    </Typography>
                                                                </Box>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    Level {b.level}
                                                                </Typography>
                                                            </Box>
                                                            <Box display="flex" height={8} borderRadius={4} overflow="hidden" mt={1}>
                                                                <Box flex={b.progress} bgcolor="rgb(127,191,77)" />
                                                                <Box flex={100 - b.progress} bgcolor="rgb(68,83,62)" />
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </Box>
                                            ))}

                                            <Box sx={{ backgroundColor: 'rgb(239,253,219)', p: 2, borderRadius: 1, mb: 3 }}>
                                                <Typography variant="subtitle1" fontWeight={700} mb={2}>
                                                    Total Real-World Impact
                                                </Typography>
                                                <Box display="flex" justifyContent="space-between">
                                                    <Box textAlign="center">
                                                        <Typography fontWeight={700}>{data.totalImpact.mangrovesRestored}</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Mangroves Restored
                                                        </Typography>
                                                    </Box>
                                                    <Box textAlign="center">
                                                        <Typography fontWeight={700}>{data.totalImpact.plasticRemoved}kg</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Plastic Removed
                                                        </Typography>
                                                    </Box>
                                                    <Box textAlign="center">
                                                        <Typography fontWeight={700}>{data.totalImpact.daysOfSchool}</Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Days of School
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </>
                                    )}
                                </ToggleCard>
                                {/* Tracking CTA */}
                                <Box
                                    component={Link}
                                    to="/engagement"
                                    sx={{
                                        textDecoration: 'none',     // remove underline
                                        color: 'inherit',           // inherit text colour
                                        backgroundColor: 'rgb(239,253,219)',
                                        p: 2,
                                        borderRadius: 1,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            backgroundColor: 'rgb(227,244,204)',
                                            textDecoration: 'none'    // ensure no underline on hover
                                        }
                                    }}
                                >
                                    <Typography fontWeight={700}>Track Your Impact <span style={{ fontWeight: 400 }}>- get exciting updates and stories!</span></Typography>
                                    <Typography>{'→'}</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Collapse>
                </Box>
            </Box>
        </Container>
    );
};

export default Gamification;
